/**
 * Mundial Prediction Engine™ — Phase 1
 *
 * Pure domain layer: types, points table, badge ladder, and helpers to
 * derive reward / window metadata. No network calls, no UI, no AI.
 */

export type MundialEventType =
  | "next_goal"
  | "next_corner"
  | "next_card"
  | "next_shot_on_target"
  | "next_attacking_team"
  | "free_kick_result"
  | "penalty_result";

export type MundialWindowSeconds = 30 | 60 | 90;

export type MundialEventStatus = "open" | "closed" | "resolved" | "cancelled";

export interface MundialPredictionEvent {
  id: string;
  match_id: string;
  event_type: MundialEventType;
  question: string;
  option_a: string;
  option_b: string;
  option_c: string | null;
  window_seconds: MundialWindowSeconds;
  points_reward: number;
  start_time: string;
  end_time: string;
  correct_answer: string | null;
  status: MundialEventStatus;
}

export interface MundialPredictionAnswer {
  id: string;
  user_id: string;
  prediction_event_id: string;
  selected_answer: string;
  points_awarded: number;
  is_correct: boolean | null;
  created_at: string;
}

/** Points awarded by window size — shorter window = bigger reward. */
export const MUNDIAL_POINTS_BY_WINDOW: Record<MundialWindowSeconds, number> = {
  30: 50,
  60: 25,
  90: 10,
};

export function pointsForWindow(window: MundialWindowSeconds): number {
  return MUNDIAL_POINTS_BY_WINDOW[window] ?? 10;
}

/** Badge ladder driven by accumulated mundial XP. */
export type MundialBadgeKey =
  | "tactical_rookie"
  | "tactical_analyst"
  | "elite_predictor"
  | "oracle_mundial";

export interface MundialBadgeDef {
  key: MundialBadgeKey;
  label: string;
  minXp: number;
  emoji: string;
}

export const MUNDIAL_BADGES: MundialBadgeDef[] = [
  { key: "tactical_rookie",  label: "Tactical Rookie",  minXp: 0,    emoji: "🎯" },
  { key: "tactical_analyst", label: "Tactical Analyst", minXp: 250,  emoji: "📊" },
  { key: "elite_predictor",  label: "Elite Predictor",  minXp: 750,  emoji: "🏅" },
  { key: "oracle_mundial",   label: "Oracle Mundial",   minXp: 2000, emoji: "🔮" },
];

export function badgeForXp(xp: number): { current: MundialBadgeDef; next: MundialBadgeDef | null; progress: number } {
  let current = MUNDIAL_BADGES[0];
  for (const b of MUNDIAL_BADGES) if (xp >= b.minXp) current = b;
  const idx = MUNDIAL_BADGES.indexOf(current);
  const next = idx < MUNDIAL_BADGES.length - 1 ? MUNDIAL_BADGES[idx + 1] : null;
  const progress = next
    ? Math.min(100, ((xp - current.minXp) / (next.minXp - current.minXp)) * 100)
    : 100;
  return { current, next, progress };
}

/** Remaining seconds before the window closes. */
export function remainingSeconds(endTime: string, now = Date.now()): number {
  const end = new Date(endTime).getTime();
  return Math.max(0, Math.floor((end - now) / 1000));
}

/** Compute points for an answer given the resolved event. */
export function computePoints(event: MundialPredictionEvent, selected: string): number {
  if (event.status !== "resolved" || !event.correct_answer) return 0;
  return event.correct_answer === selected ? event.points_reward : 0;
}

export const MUNDIAL_EVENT_LABELS: Record<MundialEventType, string> = {
  next_goal: "Próximo gol",
  next_corner: "Próximo córner",
  next_card: "Próxima tarjeta",
  next_shot_on_target: "Próximo tiro a puerta",
  next_attacking_team: "Próximo equipo que atacará",
  free_kick_result: "Resultado del tiro libre",
  penalty_result: "Resultado del penal",
};